<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            @rowSelectionChanged="preview"
            download-filename="Wideband Products"
            ajax-path="wideband/products"
            :ajax-params="ajaxParams"            
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
        />
        <app-modal 
            width="lg:w-2/3"
            bg="bg-gray-200"
            :show.sync="showPreviewModal"
            @close="showPreviewModal = false"
        >
            <template slot="header">PRODUCT DETAILS</template>
            <template slot="body">
                <div class="mb-5 grid gap-2 grid-cols-2 sm:grid-cols-3">
                    <app-button 
                        class="bg-blue-500 text-white"
                        @click="editBandwidth"
                    >Modify Bandwidth</app-button>
                    <app-button 
                        class="bg-blue-500 text-white"
                        @click="() => {
                            showEditStabilityModal = true;
                            stabilityForm.errors.clear();
                            stabilityForm.reset();
                            stabilityForm.set({
                                current: product.nbn_stability_profile,
                            });
                        }"
                        v-if="['Fibre To The Node', 'Fibre To The Building'].indexOf(product.nbn_access_technology) !== -1"
                    >Modify Stability</app-button>
                    <app-button 
                        class="bg-blue-500 text-white"
                        @click="showTestsModal = true"
                    >Tests</app-button>
                    <app-button 
                        class="bg-blue-500 text-white"
                        @click="showTroubleTicketsModal = true"
                    >Trouble Tickets</app-button>
                    <app-button 
                        class="bg-blue-500 text-white"
                        @click="disconnect"
                    >Disconnect</app-button>
                </div>
                <div class="card">
                    <table class="table">
                        <tr>
                            <td><strong>Wideband Service ID</strong></td>
                            <td>
                                <div>{{ product.id }}</div>
                            </td>
                            <td><strong>Your Reference</strong></td>
                            <td>
                                <div>{{ product.access_seeker_ref }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Status</strong></td>
                            <td>
                                <div>{{ product.status }}</div>
                            </td>
                            <td><strong>Created</strong></td>
                            <td>
                                <div>{{ product.created_at_for_human }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Access Technology</strong></td>
                            <td>
                                <div>{{ product.nbn_access_technology }}</div>
                            </td>
                            <td><strong>NBN Location ID</strong></td>
                            <td>
                                <div>{{ product.nbn_location_id }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Address</strong></td>
                            <td colspan="3">
                                <div>{{ product.formatted_address }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Bandwidth Profile</strong></td>
                            <td>
                                <div>{{ product.speed_for_human }}</div>
                                <div v-if="product.nbn_stability_profile" class="text-red-500 text-xs">Stability Profile: {{ product.nbn_stability_profile }}</div>
                            </td>
                            <td><strong>NBN CVC ID</strong></td>
                            <td>
                                <div>{{ product.nbn_cvc_id }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>NBN PRI ID</strong></td>
                            <td>
                                <div>{{ product.nbn_pri_id }}</div>
                            </td>
                            <td><strong>NBN AVC ID</strong></td>
                            <td>
                                <div>{{ product.nbn_avc_id }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>NBN C-Tag</strong></td>
                            <td>
                                <div>{{ product.nbn_ctag }}</div>
                            </td>
                            <td><strong>NBN UNI-D ID</strong></td>
                            <td>
                                <div>{{ product.nbn_unid_id }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>NBN NTD ID</strong></td>
                            <td>
                                <div>{{ product.nbn_ntd_id }}</div>
                            </td>
                            <td><strong>NBN NTD Port</strong></td>
                            <td>
                                <div>{{ product.nbn_ntd_port_id }}</div>
                            </td>
                        </tr>                    
                    </table>
                </div>
                <h2 class="mt-6 mb-3 font-thin tracking-widest uppercase flex-1">Order History</h2>
                <tabulator 
                    :data="product.order_history"
                    :columns="[
                        {
                            title: 'ID',
                            field: 'order_id',
                            width: 80,
                        },
                        {
                            title: 'Created',
                            field: 'created_at_for_human',
                            minWidth: 200,
                        },
                        {
                            title: 'Order Type',
                            field: 'order_type',
                            minWidth: 200,
                        },
                        {
                            title: 'Status',
                            field: 'status',
                            minWidth: 90,
                        }
                    ]"
                    :show-header="false"
                    :show-footer="false"
                />
                <h2 class="mt-6 mb-3 font-thin tracking-widest uppercase flex-1">Test History</h2>
                <tabulator 
                    :data="product.tests"
                    :columns="[
                        {
                            title: 'ID',
                            field: 'test_id',
                            width: 80,
                        },
                        {
                            title: 'Created',
                            field: 'created_at_for_human',
                            minWidth: 200,
                        },
                        {
                            title: 'Test Type',
                            field: 'test_type',
                            minWidth: 200,
                        },
                        {
                            title: 'Status',
                            field: 'status',
                            minWidth: 90,
                        }
                    ]"
                    :show-header="false"
                    :show-footer="false"
                />
            </template>
        </app-modal>
        <app-modal
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showEditBandwidthModal"
            @close="editBandwidthModalClosed"
        >
            <template slot="header">Modify Bandwidth</template>
            <template slot="body">
                <app-input 
                    v-model="speedForm.current"
                    placeholder="Current Speed"
                    :disable="true"
                />
                <app-select
                    v-model="speedForm.tc4speed"
                    placeholder="New Speed"
                    :error="speedForm.errors.get('tc4speed')"
                    @updated="speedForm.errors.clear('tc4speed')"
                    :options="product.available_speeds"
                    value-key="value"
                />
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showEditBandwidthModal = false"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :loading="updatingBandwidth"
                        @click="updateBandwidth"
                    >Submit</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showEditStabilityModal"
        >
            <template slot="header">Modify Stability Profile</template>
            <template slot="body">
                <app-input 
                    v-model="stabilityForm.current"
                    placeholder="Current Profile"
                    :disable="true"
                />
                <app-select
                    v-model="stabilityForm.stabilityProfile"
                    placeholder="New Profile"
                    :error="stabilityForm.errors.get('stabilityProfile')"
                    @updated="stabilityForm.errors.clear('stabilityProfile')"
                    :options="['Stable', 'Standard']"
                />
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showEditStabilityModal = false"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :loading="updatingStability"
                        @click="updateStability"
                    >Submit</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showTestsModal"
        >
            <template slot="header">Perform Tests</template>
            <template slot="body">
                <app-button 
                    class="btn-blue btn-block mb-3"
                    :loading="lineStateTesting"
                    :disabled="testButtonsDisabled"
                    @click="testLineState"
                    v-if="['Fibre To The Node'].indexOf(product.nbn_access_technology) !== -1"
                >Perform Line State Test</app-button>
                <app-button 
                    class="btn-blue btn-block mb-3"
                    :loading="lineQualityTesting"
                    :disabled="testButtonsDisabled"
                    @click="testLineQuality"
                    v-if="['Fibre To The Node'].indexOf(product.nbn_access_technology) !== -1"
                >Perform Line Quality Diagnostic Test</app-button>
                <app-button 
                    class="btn-blue btn-block mb-3"
                    :loading="singleEndLineTesting"
                    :disabled="testButtonsDisabled"
                    @click="testSingleEndLine"
                    v-if="['Fibre To The Node'].indexOf(product.nbn_access_technology) !== -1"
                >Perform Single End Line Test</app-button>
                <app-button 
                    class="btn-blue btn-block mb-3"
                    :loading="ntdIsReseting"
                    :disabled="testButtonsDisabled"
                    @click="ntdReset"
                    v-if="['HFC'].indexOf(product.nbn_access_technology) !== -1"
                >Perform NTD Reset</app-button>
                <app-button 
                    class="btn-blue btn-block mb-3"
                    :loading="isLoopbacking"
                    :disabled="testButtonsDisabled"
                    @click="loopback"
                >Perform Loopback Test</app-button>
                <app-button 
                    class="btn-blue btn-block mb-3"
                    :loading="isResetingPort"
                    :disabled="testButtonsDisabled"
                    @click="resetPort"
                    v-if="['Fibre', 'Fibre To The Curb', 'Fibre To The Node'].indexOf(product.nbn_access_technology) !== -1"
                >Perform Port Reset</app-button>
                <app-button 
                    class="btn-blue btn-block mb-3"
                    :loading="checkingNtdStatus"
                    :disabled="testButtonsDisabled"
                    @click="checkNtdStatus"
                    v-if="['Fibre', 'HFC'].indexOf(product.nbn_access_technology) !== -1"
                >Perform NTD Status Test</app-button>
                <app-button 
                    class="btn-blue btn-block"
                    :loading="testingUnidStatus"
                    :disabled="testButtonsDisabled"
                    @click="testUnidStatus"
                    v-if="['Fibre'].indexOf(product.nbn_access_technology) !== -1"
                >Perform UNI-D Status Test</app-button>
            </template>
        </app-modal>
        <app-modal
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showNtdStatusModal"
        >
            <template slot="header">NTD Status</template>
            <template slot="body">
                <table class="table" v-if="['HFC'].indexOf(product.nbn_access_technology) !== -1">
                    <tr>
                        <td>NTD Up Time</td>
                        <td>{{ ntdStatus.output['NTD Up Time Formatted'] }}</td>
                    </tr>
                    <tr>
                        <td>Link State</td>
                        <td>{{ ntdStatus.output['Link State'] }}</td>
                    </tr>
                    <tr>
                        <td>Link Type</td>
                        <td>{{ ntdStatus.output['Link Type'] }}</td>
                    </tr>
                </table>
                <table class="table" v-if="['Fibre'].indexOf(product.nbn_access_technology) !== -1">
                    <tr>
                        <td>Serial Number</td>
                        <td>{{ ntdStatus.output['Serial Number'] }}</td>
                    </tr>
                    <tr>
                        <td>Operational State</td>
                        <td>{{ ntdStatus.output['Operational State'] }}</td>
                    </tr>
                    <tr>
                        <td>Reporting State</td>
                        <td>{{ ntdStatus.output['Reporting State'] }}</td>
                    </tr>
                    <tr>
                        <td>Battery Power Type</td>
                        <td>{{ ntdStatus.output['Power Type'] }}</td>
                    </tr>
                    <tr>
                        <td>Battery Fail Status</td>
                        <td>{{ ntdStatus.output['Battery Fail Status'] }}</td>
                    </tr>
                    <tr v-if="ntdStatus.output['Battery Install Date']">
                        <td>Battery Install Date</td>
                        <td>{{ ntdStatus.output['Battery Install Date'] }}</td>
                    </tr>
                    <tr>
                        <td>Battery Missing Status</td>
                        <td>{{ ntdStatus.output['Battery Missing Status'] }}</td>
                    </tr>
                </table>
            </template>
        </app-modal>
        <app-modal
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showUnidStatusModal"
        >
            <template slot="header">UNI-D Status</template>
            <template slot="body">
                <table class="table">
                    <tr>
                        <td>Physical Port Number</td>
                        <td>{{ unidStatus.response.results['Test results on UNI-D - Resource']['UNI-D'].entityRelationships['PhysicalPort relates to UNI-D - Resource'].PhysicalPort['Port Number'] }}</td>
                    </tr>
                    <tr>
                        <td>Physical Port MAC Address</td>
                        <td>{{ unidStatus.response.results['Test results on UNI-D - Resource']['UNI-D'].entityRelationships['PhysicalPort relates to UNI-D - Resource'].PhysicalPort['MAC Address'] }}</td>
                    </tr>
                    <tr>
                        <td>Operational State</td>
                        <td>{{ unidStatus.response.results['Test results on UNI-D - Resource']['UNI-D'].entityRelationships['ResourceStateHistory relates to UNI-D - ResourceStateHistory'].ResourceStateHistory['Operational State'] }}</td>
                    </tr>
                    <tr>
                        <td>Link State</td>
                        <td>{{ unidStatus.response.results['Test results on UNI-D - Resource']['UNI-D'].entityRelationships['ResourceStateHistory relates to UNI-D - ResourceStateHistory'].ResourceStateHistory['Link State'] }}</td>
                    </tr>
                    <tr>
                        <td>Config Status</td>
                        <td>{{ unidStatus.response.results['Test results on UNI-D - Resource']['UNI-D'].entityRelationships['ResourceStateHistory relates to UNI-D - ResourceStateHistory'].ResourceStateHistory['Config Status'] }}</td>
                    </tr>
                    <tr>
                        <td>State</td>
                        <td>{{ unidStatus.response.results['Test results on UNI-D - Resource']['UNI-D'].entityRelationships['ResourceStateHistory relates to UNI-D - ResourceStateHistory'].ResourceStateHistory['State'] }}</td>
                    </tr>
                </table>
            </template>
        </app-modal>
        <app-modal
            width="lg:w-1/2"
            bg="bg-gray-200"
            :show.sync="showLineStateModal"
        >
            <template slot="header">Line State</template>
            <template slot="body">
                <div class="card mb-4">
                    <div class="px-3 py-4 uppercase tracking-widest text-xs">Service State</div>
                    <table class="table">
                        <tr>
                            <td>Operational Status</td>
                            <td>{{ lineState.output.servicestate['Operational Status'] }}</td>
                        </tr>
                        <tr>
                            <td>Service Stability</td>
                            <td>{{ lineState.output.servicestate['Service Stability'] }}</td>
                        </tr>
                        <tr>
                            <td>Fallback State</td>
                            <td>{{ lineState.output.servicestate['Fallback State'] }}</td>
                        </tr>
                        <tr>
                            <td>Physical Profile</td>
                            <td>{{ lineState.output.servicestate['Physical Profile'] }}</td>
                        </tr>
                        <tr>
                            <td>Vectoring Statue</td>
                            <td>{{ lineState.output.servicestate['Vectoring Status'] }}</td>
                        </tr>
                        <tr>
                            <td>Actual Rtx Mode Downstream</td>
                            <td>{{ lineState.output.servicestate['Actual Rtx Mode Downstream'] }}</td>
                        </tr>
                        <tr>
                            <td>Actual Rtx Mode Upstream</td>
                            <td>{{ lineState.output.servicestate['Actual Rtx Mode Upstream'] }}</td>
                        </tr>
                    </table>
                </div>
                <div class="card mb-4" v-if="lineState.output.notes && lineState.output.notes.length > 0">
                    <div class="px-3 py-4 uppercase tracking-widest text-xs">Notes</div>
                    <table class="table">
                        <tr v-for="(note, key) in lineState.output.notes" :key="key">
                            <td width="80">{{ note.id }}</td>
                            <td class="overflow-visible">{{ note.note }}</td>
                        </tr>
                    </table>
                </div>
                <div class="card mb-4">
                    <div class="px-3 py-4 uppercase tracking-widest text-xs">Copper Pair</div>
                    <table class="table">
                        <tr>
                            <td>Estimated DELT Distance</td>
                            <td>{{ lineState.output.copperpair['Estimated DELT Distance'] }}</td>
                        </tr>
                        <tr>
                            <td>Electrical Length</td>
                            <td>{{ lineState.output.copperpair['Electrical Length'] }}</td>
                        </tr>
                    </table>
                </div>
                <div class="card mb-4">
                    <div class="px-3 py-4 uppercase tracking-widest text-xs">CPE</div>
                    <table class="table">
                        <tr>
                            <td>Type</td>
                            <td>{{ lineState.output.cpe['CPE Type'] }}</td>
                        </tr>
                        <tr>
                            <td>Modem Vendor ID</td>
                            <td>{{ lineState.output.cpe['Modem Vendor Id'] }}</td>
                        </tr>
                        <tr>
                            <td>System Vendor ID</td>
                            <td>{{ lineState.output.cpe['System Vendor Id'] }}</td>
                        </tr>
                        <tr>
                            <td>System Vendor Model</td>
                            <td>{{ lineState.output.cpe['System Vendor Model'] }}</td>
                        </tr>
                        <tr>
                            <td>Actual CPE Vectoring Type</td>
                            <td>{{ lineState.output.cpe['Actual CPE Vectoring Type'] }}</td>
                        </tr>
                        <tr>
                            <td>Supported CPE Vectoring Type</td>
                            <td>{{ lineState.output.cpe['Supported CPE Vectoring Type'] }}</td>
                        </tr>
                    </table>
                </div>
                <div class="card">
                    <div class="px-3 py-4 uppercase tracking-widest text-xs">Details</div>
                    <table class="table">
                        <tr>
                            <td>Result</td>
                            <td>{{ lineState.result }}</td>
                        </tr>
                        <tr>
                            <td>Status</td>
                            <td>{{ lineState.status }}</td>
                        </tr>
                        <tr>
                            <td>DSL Mode</td>
                            <td>{{ lineState.output['DSL Mode'] }}</td>
                        </tr>
                        <tr>
                            <td>Detected MAC Address</td>
                            <td>{{ lineState.output['Detected MAC Address 1'] }}</td>
                        </tr>
                        <tr>
                            <td>RTX Actual Net Data Rate</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['RTX Actual Net Data Rate']">{{ lineState.output.performance['RTX Actual Net Data Rate'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['RTX Actual Net Data Rate']">{{ lineState.output.performance['RTX Actual Net Data Rate'].Upstream }}</span>
                            </td>
                        </tr>
                        <tr>
                            <td>RTX Actual Expected Throughput</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['RTX Actual Expected Throughput']">{{ lineState.output.performance['RTX Actual Expected Throughput'].Downstream }}</span><br>
                                Upstream: <span v-if="lineState.output.performance['RTX Actual Expected Throughput']">{{ lineState.output.performance['RTX Actual Expected Throughput'].Upstream }}</span>
                            </td>
                        </tr>
                        <tr>
                            <td>RTX Attainable Net Data Rate</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['RTX Attainable Net Data Rate']">{{ lineState.output.performance['RTX Attainable Net Data Rate'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['RTX Attainable Net Data Rate']">{{ lineState.output.performance['RTX Attainable Net Data Rate'].Upstream }}</span></td>
                        </tr>
                        <tr>
                            <td>RTX Attainable Expected Throughput</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['RTX Attainable Expected Throughput']">{{ lineState.output.performance['RTX Attainable Expected Throughput'].Downstream }}</span><br>
                                Upstream: <span v-if="lineState.output.performance['RTX Attainable Expected Throughput']">{{ lineState.output.performance['RTX Attainable Expected Throughput'].Upstream }}</span></td>
                        </tr>
                        <tr>
                            <td>Actual PSD</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['Actual PSD']">{{ lineState.output.performance['Actual PSD'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['Actual PSD']">{{ lineState.output.performance['Actual PSD'].Upstream }}</span></td>
                        </tr>
                        <tr>
                            <td>Noise Margin Average</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['Noise Margin Average']">{{ lineState.output.performance['Noise Margin Average'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['Noise Margin Average']">{{ lineState.output.performance['Noise Margin Average'].Upstream }}</span></td>
                        </tr>
                        <tr>
                            <td>Attenuation Average</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['Attenuation Average']">{{ lineState.output.performance['Attenuation Average'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['Attenuation Average']">{{ lineState.output.performance['Attenuation Average'].Upstream }}</span></td>
                        </tr>
                        <tr>
                            <td>Loop Attenuation Average</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['Loop Attenuation Average']">{{ lineState.output.performance['Loop Attenuation Average'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['Loop Attenuation Average']">{{ lineState.output.performance['Loop Attenuation Average'].Upstream }}</span></td>
                        </tr>
                        <tr>
                            <td>Relative Capacity Occupation</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['Relative Capacity Occupation']">{{ lineState.output.performance['Relative Capacity Occupation'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['Relative Capacity Occupation']">{{ lineState.output.performance['Relative Capacity Occupation'].Upstream }}</span></td>
                        </tr>
                        <tr>
                            <td>Output Power</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['Output Power']">{{ lineState.output.performance['Output Power'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['Output Power']">{{ lineState.output.performance['Output Power'].Upstream }}</span></td>
                        </tr>
                        <tr>
                            <td>User Traffic</td>
                            <td>
                                Downstream: <span v-if="lineState.output.performance['User Traffic']">{{ lineState.output.performance['User Traffic'].Downstream }}</span><br> 
                                Upstream: <span v-if="lineState.output.performance['User Traffic']">{{ lineState.output.performance['User Traffic'].Upstream }}</span></td>
                        </tr>
                    </table>
                </div>
            </template>
        </app-modal>
        <app-modal
            width="lg:w-1/2"
            :show.sync="showTroubleTicketsModal"
        >
            <template slot="header">TROUBLE TICKET HISTORY</template>
            <template slot="body">                
                <tabulator 
                    :data="product.trouble_tickets"
                    :columns="[
                        {
                            title: 'ID',
                            field: 'ticket_id',
                            minWidth: 90
                        },
                        {
                            title: 'Reference',
                            field: 'access_seeker_ref',
                            minWidth: 200,
                        },
                        {
                            title: 'Created',
                            field: 'created_at_for_human',
                            minWidth: 200,
                        },
                        {
                            title: 'Last Updated',
                            field: 'updated_at_for_human',
                            minWidth: 200,
                        },
                        {
                            title: 'Category',
                            field: 'category',
                            minWidth: 300,
                        },
                        {
                            title: 'Status',
                            field: 'status',
                            minWidth: 100,
                        }
                    ]"
                    :show-header="false"
                    :show-footer="false"
                />
                <div class="text-right mt-5">
                    <app-button class="btn-blue" @click="createTicket">Lodge Trouble Ticket</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showCreateTicketModal"
            @close="createTicketModalClosed"
        >
            <template slot="header">Lodge Trouble Ticket</template>
            <template slot="body">
                <app-input 
                    v-model="createTicketForm.accessSeekerRef"
                    placeholder="Reference"
                    :error="createTicketForm.errors.get('accessSeekerRef')"
                    @updated="createTicketForm.errors.clear('accessSeekerRef')"
                />
                <app-select
                    v-model="createTicketForm.severity"
                    placeholder="Severity"
                    :error="createTicketForm.errors.get('severity')"
                    @updated="createTicketForm.errors.clear('severity')"
                    :options="['Low', 'Medium', 'High', 'Critical']"
                    required
                />
                <app-select
                    v-model="createTicketForm.engagementType"
                    placeholder="Engagement Type"
                    :error="createTicketForm.errors.get('engagementType')"
                    @updated="createTicketForm.errors.clear('engagementType')"
                    :options="['Appointment', 'Commitment', 'Inconclusive']"
                    required
                />
                <app-select
                    v-model="createTicketForm.category"
                    placeholder="Category"
                    :error="createTicketForm.errors.get('category')"
                    @updated="createTicketForm.errors.clear('category')"
                    :options="product.trouble_ticket_categories"
                    required
                />
                <template v-for="(item, i) in product.trouble_ticket_descriptions">
                    <div v-if="i === 'ntd_led'" class="mt-6" :key="i">
                        <h2 class="font-thin tracking-widest uppercase">NTD LEDs</h2>
                        <div style="display: grid;grid-column-gap:10px;grid-template-columns: 1fr 1fr;">
                            <template v-for="(led, j) in item">
                                <app-select 
                                    v-if="led.input_type === 'select'"
                                    v-model="createTicketForm.description[j]"
                                    :key="j"
                                    :placeholder="j"
                                    :options="led.options"
                                />
                            </template>
                        </div>
                    </div>
                    <div v-else-if="i === 'cpe'" class="mt-6" :key="i">
                        <h2 class="font-thin tracking-widest uppercase">CPE Information</h2>
                        <template v-for="(cpe, k) in item">
                            <app-select 
                                v-if="cpe.input_type === 'select'"
                                v-model="createTicketForm.description[k]"
                                :key="k"
                                :placeholder="k"
                                :options="cpe.options"
                            />
                            <app-input 
                                v-if="cpe.input_type === 'text'"
                                v-model="createTicketForm.description[k]"
                                :key="k"
                                :placeholder="k"
                                :help-text="cpe.help_text"
                            />
                        </template>
                    </div>
                    <template v-else>
                        <app-select 
                            v-if="item.input_type === 'select'"
                            v-model="createTicketForm.description[i]"
                            :key="i"
                            :placeholder="i"
                            :options="item.options"
                        />
                        <app-input 
                            v-if="item.input_type === 'text'"
                            v-model="createTicketForm.description[i]"
                            :key="i"
                            :placeholder="i"
                        />
                        <app-textarea 
                            v-if="item.input_type === 'textarea'"
                            v-model="createTicketForm.description[i]"
                            :key="i"
                            :placeholder="i"
                        />
                        <div v-if="item.input_type === 'checkbox'" :key="i">
                            <h4 class="mt-4 mb-1 text-sm text-gray-600">{{ i }}</h4>
                            <div class="flex items-center">
                                <checkbox
                                    v-for="(option, k) in item.options"
                                    v-model="createTicketForm.description[i]"
                                    :alt-value="option"
                                    :key="k"
                                    class="mr-2"
                                >{{ option }}</checkbox>
                            </div>
                        </div>
                    </template>
                </template>
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showCreateTicketModal = false"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :loading="creatingTicket"
                        @click="submitFault"
                    >Submit Fault</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import debounce from 'lodash/debounce';
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';

    export default {
        name: 'widebandProducts',
        data() {
            return {
                ajaxParams: {type: 'active'},
                showPreviewModal: false,
                showEditBandwidthModal: false,
                showEditStabilityModal: false,
                showTestsModal: false,
                showNtdStatusModal: false,
                showUnidStatusModal: false,
                showCreateTicketModal: false,
                showTroubleTicketsModal: false,
                showLineStateModal: false,
                tableIsLoading: false,
                tableLoadingText: null,
                ntdIsReseting: false,
                lineStateTesting: false,
                lineQualityTesting: false,
                singleEndLineTesting: false,
                isLoopbacking: false,
                isResetingPort: false,
                checkingNtdStatus: false,
                testingUnidStatus: false,
                creatingTicket: false,
                refreshTable: false,
                testButtonsDisabled: false,
                updatingStability: false,
                ntdStatus: {
                    output: {}
                },
                unidStatus: {
                    response: {
                        results: {
                            'Test results on UNI-D - Resource': {
                                'UNI-D': {
                                    entityRelationships: {
                                        'PhysicalPort relates to UNI-D - Resource': {
                                            PhysicalPort: {

                                            }
                                        },
                                        'ResourceStateHistory relates to UNI-D - ResourceStateHistory': {
                                            ResourceStateHistory: {
                                                
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                lineState: {
                    output: {
                        servicestate: {},
                        copperpair: {},
                        cpe: {},
                        performance: {
                            'RTX Actual Net Data Rate': {},
                            'RTX Actual Expected Throughput': {},
                            'RTX Attainable Net Data Rate': {},
                            'RTX Attainable Expected Throughput': {},
                            'Actual PSD': {},
                            'Noise Margin Average': {},
                            'Attenuation Average': {},
                            'Loop Attenuation Average': {},
                            'Relative Capacity Occupation': {},
                            'Output Power': {},
                            'User Traffic': {},
                        }
                    }
                },
                q: null,
                columns: [
                    {
                        title: 'ID',
                        field: 'id',
                        sorter: 'string',
                        width: 90,
                    },
                    {
                        title: 'Created',
                        field: 'created_at_for_human',
                        sorter: 'string',
                        minWidth: 190,
                    },
                    {
                        title: 'Access Technology',
                        field: 'nbn_access_technology',
                        sorter: 'string',
                        minWidth: 170,
                    },
                    {
                        title: 'NBN Location ID',
                        field: 'nbn_location_id',
                        sorter: 'string',
                        minWidth: 170,
                    },
                    {
                        title: 'NBN AVC ID',
                        field: 'nbn_avc_id',
                        minWidth: 170,
                    },
                    {
                        title: 'Username',
                        field: 'access_seeker_ref',
                        sorter: 'string',
                        minWidth: 200,
                    }
                ],
                actions: [
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync,
                    }
                ],
                speedForm: new Form({
                    id: null,
                    current: null,
                    tc4speed: null,
                }),
                stabilityForm: new Form({
                    current: null,
                    stabilityProfile: null,
                }),
                updatingBandwidth: false,
                createTicketForm: new Form({
                    accessSeekerRef: null,
                    severity: null,
                    description: {},
                    category: null,
                    engagementType: null,
                    widebandAppoinmentId: null,
                })
            }
        },
        computed: {
            ...mapGetters({
                products: 'wideband/products/items',
                product: 'wideband/products/selected',
            })
        },
        methods: {            
            preview(products) {
                if (products.length) {
                    this.showPreviewModal = true;
                    this.$store.commit('wideband/products/setSelected', products[0]);
                }
            },            
            editBandwidth() {
                this.showEditBandwidthModal = true;
                this.speedForm.set({
                    id: this.product.id,
                    current: this.product.speed_for_human,
                });
            },
            updateBandwidth() {
                this.updatingBandwidth = true;
                this.$store.dispatch('wideband/products/updateBandwidth', this.speedForm.payload())
                    .then(() => {
                        this.updatingBandwidth = false;
                        this.showEditBandwidthModal = false;
                        this.speedForm.reset();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Modify bandwidth order was successfully submitted.'
                        })
                    })
                    .catch(({ response }) => {
                        this.updatingBandwidth = false;
                        this.speedForm.errors.record(response.data);
                    })
            },
            editBandwidthModalClosed() {
                this.speedForm.reset();
            },
            updateStability() {
                this.updatingStability = true;
                Http.patch(`wideband/products/${this.product.id}/stability`, this.stabilityForm.payload())
                    .then(() => {
                        this.updatingStability = false;
                        this.showEditStabilityModal = false;
                        this.stabilityForm.reset();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Modify stability order was successfully submitted.'
                        })
                    })
                    .catch(({ response }) => {
                        this.updatingStability = false;
                        this.stabilityForm.errors.record(response.data);
                    })
            },
            ntdReset() {
                this.testButtonsDisabled = true;
                this.ntdIsReseting = true;
                this.$store.dispatch('wideband/products/ntdReset')
                    .then(() => {
                        this.testButtonsDisabled = false;
                        this.ntdIsReseting = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'NTD was reset successfully.'
                        });
                    })
                    .catch(() => {
                        this.testButtonsDisabled = false;
                        this.ntdIsReseting = false;
                    })
            },
            loopback() {
                this.testButtonsDisabled = true;
                this.isLoopbacking = true;
                this.$store.dispatch('wideband/products/loopback')
                    .then(() => {
                        this.testButtonsDisabled = false;
                        this.isLoopbacking = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Loopback test was successful.'
                        });
                    })
                    .catch(() => {
                        this.testButtonsDisabled = false;
                        this.isLoopbacking = false;
                    })
            },
            resetPort() {
                this.isResetingPort = true;
                this.testButtonsDisabled = true;
                Http.post(`wideband/products/${this.product.id}/resetPort`)
                    .then(() => {
                        this.isResetingPort = false;
                        this.testButtonsDisabled = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Port was reset successfully.'
                        });
                    })
                    .catch(() => {
                        this.isResetingPort = false;
                        this.testButtonsDisabled = false;
                    })
            },
            testUnidStatus() {
                this.testingUnidStatus = true;
                this.testButtonsDisabled = true;
                Http.post(`wideband/products/${this.product.id}/unidStatus`)
                    .then(({data}) => {
                        this.testingUnidStatus = false;
                        this.testButtonsDisabled = false;
                        this.unidStatus = data;
                        this.showUnidStatusModal = true;
                    })
                    .catch(() => {
                        this.testingUnidStatus = false;
                        this.testButtonsDisabled = false;
                    })
            },
            testLineState() {
                this.lineStateTesting = true;
                this.testButtonsDisabled = true;
                Http.post(`wideband/products/${this.product.id}/lineState`)
                    .then(({data}) => {
                        this.lineStateTesting = false;
                        this.testButtonsDisabled = false;
                        this.lineState = data;
                        this.showLineStateModal = true;
                    })
                    .catch(() => {
                        this.lineStateTesting = false;
                        this.testButtonsDisabled = false;
                    })
            },
            testLineQuality() {
                this.lineQualityTesting = true;
                this.testButtonsDisabled = true;
                Http.post(`wideband/products/${this.product.id}/lqd`)
                    .then(() => {
                        this.lineQualityTesting = false;
                        this.testButtonsDisabled = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Line quality test was successfully submitted.'
                        });
                    })
                    .catch(() => {
                        this.lineQualityTesting = false;
                        this.testButtonsDisabled = false;
                    })
            },
            testSingleEndLine() {
                this.singleEndLineTesting = true;
                this.testButtonsDisabled = true;
                Http.post(`wideband/products/${this.product.id}/selt`)
                    .then(() => {
                        this.singleEndLineTesting = false;
                        this.testButtonsDisabled = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Single end line test was successfully submitted.'
                        });
                    })
                    .catch(() => {
                        this.singleEndLineTesting = false;
                        this.testButtonsDisabled = false;
                    })
            },
            checkNtdStatus() {
                this.testButtonsDisabled = true;
                this.checkingNtdStatus = true;
                this.$store.dispatch('wideband/products/ntdStatus')
                    .then(({data}) => {
                        this.testButtonsDisabled = false;
                        this.checkingNtdStatus = false;
                        this.showNtdStatusModal = true;
                        this.ntdStatus = data;
                    })
                    .catch(() => {
                        this.testButtonsDisabled = false;
                        this.checkingNtdStatus = false;
                    })
            },
            createTicket() {
                this.showCreateTicketModal = true;
                let description = {};

                for (let i in this.product.trouble_ticket_descriptions) {
                    if (i === 'ntd_led' || i === 'cpe') {
                        for (let j in this.product.trouble_ticket_descriptions[i]) {
                            description[j] = null;
                        }
                    } else {
                        if (this.product.trouble_ticket_descriptions[i].input_type === 'checkbox') {
                            description[i] = [];
                        } else {
                            description[i] = null;
                        }
                    }
                }

                this.createTicketForm.set({description});
            },
            createTicketModalClosed() {
                this.createTicketForm.reset();
                this.createTicketForm.errors.clear();
            },
            submitFault() {
                this.creatingTicket = true;
                this.$store.dispatch('wideband/products/submitTroubleTicket', this.createTicketForm.payload())
                    .then(() => {
                        this.refreshTable = true;
                        this.creatingTicket = false;
                        this.showCreateTicketModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Trouble ticket was successfully submitted.'
                        })
                    })
                    .catch(({ response }) => {
                        this.creatingTicket = false;
                        this.createTicketForm.errors.record(response.data);

                        if (this.createTicketForm.errors.count() > 0) {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: 'One of the inputs are invalid.'
                            });
                        }
                    });
            },
            disconnect() {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to disconnect Wideband service ${this.product.id}.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, disconnect it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('wideband/products/disconnect');
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.getUsers();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Disconnect order was successfully submitted.'
                        })
                    }
                })
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                this.$store.dispatch('wideband/products/sync', 'active')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.'
                        });
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            }
        }
    }
</script>